// src/components/layout/AppLayout.tsx
import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, BarChart3, Settings, LogOut, BookOpen, PenTool, Fingerprint } from 'lucide-react';
import { signOut, getCurrentUser } from '../../services/firebase';
import { cn } from '../../lib/utils';
import { BrandSettingsModal } from '../BrandSettingsModal'; // Ensure this path matches where you saved the modal

interface AppLayoutProps {
  children?: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getCurrentUser();
  
  // NEW: State to control the Brand Settings Modal
  const [showThemeModal, setShowThemeModal] = useState(false);

  const initials = user?.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0].toUpperCase() ?? '?';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#333333] font-sans flex flex-col relative">
      
      {/* ── FLOATING PILL HEADER ── */}
      <header className="pointer-events-none fixed top-0 left-0 right-0 p-4 md:p-5 z-40 flex justify-center">
        <div className="pointer-events-auto relative w-full max-w-5xl h-[60px] px-4 flex items-center justify-between border border-slate-200 bg-white/90 backdrop-blur-md shadow-sm rounded-[999px] transition-all duration-300">
          
          {/* 1. LEFT: Logo (flex-1 forces it to take equal space as the right side) */}
          <div className="flex items-center flex-1">
            <Link to="/dashboard" className="flex items-center gap-2 group">
              <span className="w-2.5 h-2.5 rounded-full bg-[#333333] inline-block group-hover:opacity-70 transition-opacity" />
              <span className="font-bold text-[17px] tracking-tight text-[#000000]" style={{ fontFamily: 'Funnel Sans, sans-serif' }}>
                Buildlog
              </span>
            </Link>
          </div>

          {/* 2. CENTER: Main Navigation */}
          <nav className="hidden md:flex items-center justify-center gap-1 shrink-0">
            <NavLink to="/dashboard" icon={<LayoutGrid size={16} />} label="Projects"   active={location.pathname === '/dashboard'} />
            <NavLink to="/content"   icon={<PenTool size={16} />}    label="Content"    active={location.pathname === '/content'} />
            <NavLink to="/analytics" icon={<BarChart3 size={16} />}  label="Analytics"  active={location.pathname === '/analytics'} />
            <NavLink to="/strategy"  icon={<BookOpen size={16} />}   label="Strategy"   active={location.pathname === '/strategy'} />
          </nav>

          {/* 3. RIGHT: Actions & Profile (flex-1 and justify-end push contents to the right edge) */}
          <div className="flex items-center justify-end flex-1 gap-1 sm:gap-2">
            
            {/* NEW: Brand Settings Trigger */}
            <button
              onClick={() => setShowThemeModal(true)}
              className="p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              title="Brand Identity Configuration"
            >
              <Fingerprint size={18} />
            </button>

            <Link
              to="/settings"
              className={cn(
                'p-2 rounded-full text-slate-500 hover:bg-slate-100 transition-colors',
                location.pathname === '/settings' && 'bg-slate-100 text-[#000000]'
              )}
              title="System Settings"
            >
              <Settings size={18} />
            </Link>

            <div className="h-6 w-px bg-slate-200 mx-1" />

            <div className="flex items-center gap-2 pl-1">
              {user?.photoURL ? (
                <img src={user.photoURL} alt={user.displayName ?? 'User'} className="w-8 h-8 rounded-full border border-slate-200" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-medium text-xs border border-slate-200">
                  {initials}
                </div>
              )}
              <button
                onClick={handleSignOut}
                className="hidden sm:flex p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-colors"
                title="Sign Out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>

        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      {/* Added pt-28 (padding-top) so content clears the fixed floating header */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12">
        {children ?? <Outlet />}
      </main>
      
      {/* NEW: Render the Modal when active */}
      {showThemeModal && (
        <BrandSettingsModal onClose={() => setShowThemeModal(false)} />
      )}
    </div>
  );
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

function NavLink({ to, icon, label, active }: { to: string; icon: React.ReactNode; label: string; active: boolean }) {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
        active 
          ? 'bg-slate-100 text-[#000000]' 
          : 'text-[#6C6864] hover:bg-slate-50 hover:text-[#000000]'
      )}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}